import { useCallback, useEffect, useState } from "react";

/**
 * useTheme — reads/writes the user's theme choice ("light" | "dark") in
 * localStorage and applies the corresponding class to <html>. The chosen
 * class is also applied by a tiny inline script in public/index.html
 * BEFORE React mounts, so there is no flash of the wrong theme.
 *
 * Oracle keeps its own dusk sub-theme regardless of this setting —
 * .oracle-theme uses --oracle-* variables which are not overridden by .dark.
 */
const STORAGE_KEY = "correlate.theme";

function readInitial() {
  if (typeof window === "undefined") return "light";
  try {
    const t = window.localStorage.getItem(STORAGE_KEY);
    if (t === "dark" || t === "light") return t;
  } catch (err) {
    // localStorage can throw in some private/embedded contexts — fall back.
    console.warn("useTheme: localStorage read failed", err);
  }
  return "light";
}

function applyToDocument(theme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

export function useTheme() {
  const [theme, setThemeState] = useState(readInitial);

  useEffect(() => {
    applyToDocument(theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (next !== "light" && next !== "dark") return;
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (err) {
      console.warn("useTheme: localStorage write failed", err);
    }
  }, []);

  return { theme, setTheme };
}
